import { theme as BeeinventorTheme } from '@beeinventor/dasiot-react-component-lib';
import { createTheme, SxProps } from '@mui/material';
import '@mui/x-date-pickers/themeAugmentation';

const theme = createTheme({
  ...BeeinventorTheme,
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        input: {
          colorScheme: 'dark',
        },
        body: {
          color: 'white',
          backgroundColor: BeeinventorTheme.color.secondary.$100,
          fontFamily:
            '"Noto Sans TC", "Noto Sans",Roboto,Helvetica,Arial,sans-serif',
        },
      },
    },
    // The year/month picker buttons inherit their font from
    // typography.subtitle1, which this theme repurposes for large
    // headline text (2.5rem). That overflows the pickers' fixed
    // 72x36 buttons and makes the year/month text overlap, so give
    // them their own font size here instead of touching subtitle1.
    MuiYearCalendar: {
      styleOverrides: {
        button: {
          fontSize: '1rem',
          lineHeight: 1.75,
          fontWeight: 400,
        },
      },
    },
    MuiMonthCalendar: {
      styleOverrides: {
        button: {
          fontSize: '1rem',
          lineHeight: 1.75,
          fontWeight: 400,
        },
      },
    },
  },
  externalTypography: {
    body3: {
      fontSize: '0.75rem',
      lineHeight: 1.5,
    },
    body3Bold: {
      fontSize: '0.75rem',
      lineHeight: 1.5,
      fontWeight: 700,
    },
    subtitle3: {
      fontSize: '1.5rem',
      lineHeight: 1.3,
    },
  },
  externalColor: {
    red: {
      $100: '#FF6B00',
      $120: '#CC0000',
    },

    green: {
      $100: '#78DC00',
    },
    primary: {
      $60: '#FFCE4F',
    },
    secondary: {
      $140: '#262626',
      $120: '#333333',
    },
    randrom: {
      $1: '#58B99E',
      $2: '#E96363',
      $3: '#EF8C34',
      $4: '#5296D5',
      $5: '#B152C6',
      $6: '#1C4252',
    },
  },
  palette: {
    ...BeeinventorTheme.palette,
    success: {
      main: '#78DC00',
    },
    error: {
      main: '#FF6B00',
    },
  },
  typography: {
    fontFamily: '"Noto Sans TC", "Noto Sans"',
    h1: {
      fontSize: '1.75rem',
      lineHeight: 1.5,
      fontWeight: 700,
    },
    h2: {
      fontSize: '1.25rem',
      lineHeight: 1.5,
      fontWeight: 700,
    },
    h3: {
      fontSize: '1rem',
      lineHeight: 1.5,
      fontWeight: 700,
    },
    h4: {
      fontSize: '0.875rem',
      lineHeight: 1.5,
      fontWeight: 700,
    },
    subtitle1: {
      fontSize: '2.5rem',
      lineHeight: 1.2,
    },
    subtitle2: {
      fontSize: '1.875rem',
      lineHeight: 1.3,
      fontWeight: 'normal',
    },
    body1: {
      fontSize: '1rem',
      lineHeight: 1.5,
    },
    body2: {
      fontSize: '0.875rem',
      lineHeight: 1.5,
    },
    caption: {
      fontSize: '0.625rem',
      lineHeight: 1.5,
    },
  },
});

declare module '@mui/material/styles' {
  interface Theme {
    externalTypography: {
      body3?: React.CSSProperties;
      body3Bold?: React.CSSProperties;
      subtitle3?: React.CSSProperties;
    };
    externalColor: {
      dark: {
        $15: string;
        $20: string;
      };
      red: {
        $100: string;
        $120: string;
      };
      green: {
        $100: string;
      };
      primary: {
        $60: string;
      };
      secondary: {
        $140: string;
        $120: string;
      };
      randrom: {
        $1: string;
        $2: string;
        $3: string;
        $4: string;
        $5: string;
        $6: string;
      };
    };
  }

  interface ThemeOptions {
    externalColor?: {
      default?: string;
      red?: {
        $100?: string;
        $120?: string;
      };
      green?: {
        $100?: string;
      };
      primary?: {
        $60?: string;
      };
      secondary?: {
        $140?: string;
        $120?: string;
      };
      randrom?: {
        $1?: string;
        $2?: string;
        $3?: string;
        $4?: string;
        $5?: string;
        $6?: string;
      };
    };
    externalTypography?: {
      body3?: React.CSSProperties;
      body3Bold?: React.CSSProperties;
      subtitle3?: React.CSSProperties;
    };
  }
}

export const datePickerPopperSx: SxProps = {
  '& .MuiPaper-root': {
    color: 'white',
    backgroundColor: theme.color.secondary.$100,
  },
  '& .MuiPickersDay-root': {
    color: 'white',
    backgroundColor: 'black',
    '&:hover': {
      background: theme.palette.primary.light,
    },
    '&.Mui-selected': {
      backgroundColor: theme.color.primary.$100,
    },
  },
  '& .MuiButtonBase-root': {
    color: 'white',
  },
  '& .MuiTypography-root': {
    color: 'white',
  },
  '& .PrivatePickersYear-yearButton': {
    fontSize: '1.5rem',
    '&:hover': {
      background: theme.palette.primary.light,
    },
  },
};

export const memberButtonSx: SxProps = {
  fontSize: '0.75px',
  padding: '1px 4px 1px 12px',
  '& > span': {
    margin: 0,
  },
};

export const inputSx: SxProps = {
  display: 'block',
  width: '100%',
};

export const editDeviceInputSx: SxProps = {
  '& input': {
    backgroundColor: theme.color.box_bbg,
  },
};

export const rmrIssueCloseButtonSx: SxProps = {
  cursor: 'pointer',
  position: 'absolute',
  display: 'block',
  top: 0,
  right: 0,
  width: '40px',
  height: '40px',
  zIndex: 1,
};

export default theme;
